<script lang="ts">
import { defineComponent } from 'vue'
export default defineComponent({
  name: 'wangEditor',
})
</script>
<template>
  <div class="editor-container" :class="{ 'is-fullscreen': isFullscreen }">
    <Toolbar v-if="editorRef" :editor="editorRef" :mode="mode" :defaultConfig="state.toolbarConfig" />
    <div class="editor-wrapper">
      <Editor :mode="mode" :defaultConfig="state.editorConfig" :style="{ height: isFullscreen ? '100%' : height }"
        v-model="state.editorVal" @onCreated="handleCreated" @onChange="handleChange"
        @customPaste="handleCustomPaste" />
      <div v-if="uploadingCount" class="upload-loading">
        <span class="upload-loading-spinner"></span>
        <span>上传中...</span>
      </div>
    </div>
    <button type="button" class="fullscreen-btn" title="全屏" @click="toggleFullscreen">
      <FullscreenOutlined v-if="!isFullscreen" />
      <FullscreenExitOutlined v-else />
    </button>
  </div>
</template>

<script setup lang="ts" name="wangEditor">
import '@wangeditor/editor/dist/css/style.css'
import { reactive, shallowRef, ref, watch, onMounted, onBeforeUnmount, computed, nextTick } from 'vue'
import { IDomEditor, IToolbarConfig } from '@wangeditor/editor'
import { Toolbar, Editor } from '@wangeditor/editor-for-vue'
import { FullscreenOutlined, FullscreenExitOutlined } from '@ant-design/icons-vue'
import storage from '@/utils/Storage'
import { ACCESS_TOKEN } from '@/store/mutation-types'

const props = defineProps({
  disable: {
    type: Boolean,
    default: () => false
  },
  placeholder: {
    type: String,
    default: () => '请输入内容...'
  },
  mode: {
    type: String,
    default: () => 'default'
  },
  height: {
    type: String,
    default: () => '310px'
  },
  enableVideoUpload: {
    type: Boolean,
    default: () => true
  },
  normalizeBlankLines: {
    type: Boolean,
    default: () => false
  },
  getHtml: String,
  getText: String
})

const emit = defineEmits(['update:getHtml', 'update:getText'])

const editorRef = shallowRef()
const isFullscreen = ref(false)
const uploadingCount = ref(0)
const baseURL = ref((import.meta.env.VITE_PROXY || '').replace(/\/+$/, ''))
const uploadServerUrl = computed(() => {
  return baseURL.value ? `${baseURL.value}/console/file/uploadFile` : '/console/file/uploadFile'
})

const createHtmlContainer = (html?: string) => {
  const container = document.createElement('div')
  container.innerHTML = (html || '').trim()
  return container
}

const isBlankOnlyHtml = (container: HTMLDivElement) => {
  const text = (container.textContent || '').replace(/\s|\u00a0|\u200b|\u200c|\u200d|\ufeff/g, '')
  const hasNonTextContent = Boolean(container.querySelector('img, video, audio, iframe, embed, object, svg, canvas, table, hr'))
  return !text && !hasNonTextContent
}

// WangEditor 内部始终需要一个基础空段落；对外值不包含这个基础段落。
const toExternalHtml = (html?: string) => {
  if (!props.normalizeBlankLines) return html

  const value = (html || '').trim()
  if (!value) return value

  const container = createHtmlContainer(value)
  if (!isBlankOnlyHtml(container)) return value

  container.firstElementChild?.remove()
  return container.innerHTML.trim()
}

const toEditorHtml = (html?: string) => {
  if (!props.normalizeBlankLines) return html

  const value = (html || '').trim()
  if (!value) return value

  const container = createHtmlContainer(value)
  if (!isBlankOnlyHtml(container)) return value

  return `<p><br></p>${value}`
}

const uploadFile = async (file: File) => {
  const formData = new FormData()
  formData.append('file', file)
  uploadingCount.value += 1

  try {
    const res = await fetch(uploadServerUrl.value, {
      method: 'POST',
      headers: {
        Authorization: storage.get(ACCESS_TOKEN) || ''
      },
      body: formData
    })

    if (!res.ok) {
      throw new Error(`图片上传失败：${res.status}`)
    }

    const data = await res.json()
    return data.data?.url || data.data
  } finally {
    uploadingCount.value -= 1
  }
}

const transferImageUrl = async (url: string) => {
  const imageRes = await fetch(url, {
    referrerPolicy: 'no-referrer'
  })

  if (!imageRes.ok) {
    throw new Error(`图片下载失败：${imageRes.status}`)
  }

  const blob = await imageRes.blob()
  const ext = blob.type.split('/')[1] || 'png'
  const file = new File([blob], `paste-image-${Date.now()}.${ext}`, { type: blob.type || 'image/png' })
  return uploadFile(file)
}

type InsertFnType = (url: string, alt: string, href: string) => void
// type InsertVideoFnType = (url: string, poster?: string) => void

const state = reactive({
  editorConfig: {
    placeholder: props.placeholder,
    readOnly: props.disable,
    MENU_CONF: {
      uploadImage: {
        maxFileSize: 10 * 1024 * 1024,
        maxNumberOfFiles: 9,
        allowedFileTypes: ['image/*'],
        async customUpload(file: File, insertFn: InsertFnType) {
          try {
            const url = await uploadFile(file)
            insertFn(url, 'image', url)
          } catch (error) {
            console.error('图片上传失败：', error)
          }
        }
      },
      // uploadVideo: {
      //   server: uploadServerUrl.value,
      //   fieldName: 'file',
      //   headers: {
      //     Authorization: storage.get(ACCESS_TOKEN) || ''
      //   },
      //   maxFileSize: 100 * 1024 * 1024,
      //   maxNumberOfFiles: 1,
      //   allowedFileTypes: ['video/*'],
      //   customInsert(res: any, insertFn: InsertVideoFnType) {
      //     const url = res.data?.url || res.data
      //     insertFn(url)
      //   }
      // },
      insertLink: {
        parseLinkUrl(url: string) {
          if (!/^https?:\/\//i.test(url)) return 'https://' + url
          return url
        }
      }
    }
  },
  toolbarConfig: {
    excludeKeys: [
      'insertTable',
      'codeBlock',
      'fullScreen',
      'uploadVideo',
      'insertVideo',
      'group-video',
      'insertImage',
      'emotion'
    ]
  } as Partial<IToolbarConfig>,
  editorVal: toEditorHtml(props.getHtml)
})

const handleCustomPaste = async (editor: IDomEditor, event: ClipboardEvent) => {
  const html = event.clipboardData?.getData('text/html')

  if (!html) {
    return true
  }

  const hasRemoteImage = /<img[\s\S]+?src=["']https?:\/\//i.test(html)

  if (!hasRemoteImage) {
    return true
  }

  event.preventDefault()

  try {
    const newHtml = await transferImagesInHtml(html)
    editor.dangerouslyInsertHtml(newHtml)
  } catch (error) {
    console.error('粘贴图片转存失败：', error)
  }

  return false
}

const transferImagesInHtml = async (html: string) => {
  const doc = new DOMParser().parseFromString(html, 'text/html')
  const imgs = Array.from(doc.querySelectorAll('img'))

  await Promise.all(
    imgs.map(async (img) => {
      const oldUrl = img.getAttribute('src')
      if (!oldUrl) return
      if (!shouldTransferImage(oldUrl)) return

      try {
        const newUrl = await transferImageUrl(oldUrl)
        if (!newUrl) return

        img.setAttribute('src', newUrl)
        img.removeAttribute('data-src')
        img.removeAttribute('data-original')
        img.removeAttribute('data-href')
      } catch (error) {
        console.error('图片转存失败：', oldUrl, error)
      }
    })
  )

  return doc.body.innerHTML
}

const ownImageDomains = [
  'https://backup0527.obs.cn-east-3.myhuaweicloud.com'
]

const isOwnImageUrl = (url: string) => {
  return ownImageDomains.some((domain) => url.includes(domain))
}

const shouldTransferImage = (url: string) => {
  if (!/^https?:\/\//i.test(url)) return false
  if (isOwnImageUrl(url)) return false
  return true
}

const handleCreated = (editor: IDomEditor) => {
  editorRef.value = editor
}

const handleChange = (editor: IDomEditor) => {
  emit('update:getHtml', toExternalHtml(editor.getHtml()))
  emit('update:getText', editor.getText())
}

const toggleFullscreen = () => {
  isFullscreen.value = !isFullscreen.value
}

const onKeyDown = (e: KeyboardEvent) => {
  if (e.key === 'Escape' && isFullscreen.value) {
    isFullscreen.value = false
  }
}

onMounted(() => {
  document.addEventListener('keydown', onKeyDown)
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKeyDown)
  const editor = editorRef.value
  if (editor == null) return
  editor.destroy()
})

watch(
  () => props.disable,
  (bool) => {
    const editor = editorRef.value
    if (editor == null) return
    bool ? editor.disable() : editor.enable()
  },
  { deep: true }
)

watch(
  () => props.getHtml,
  (val) => {
    const editorHtml = toEditorHtml(val)
    if (state.editorVal !== editorHtml) {
      state.editorVal = editorHtml
    }
  },
  { deep: true }
)
</script>

<style scoped>
.editor-container {
  border: 1px solid #C5C5D3;
  border-radius: 4px;
  overflow: hidden;
  position: relative;
}

.editor-wrapper {
  position: relative;
}

.upload-loading {
  position: absolute;
  inset: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: rgba(0, 0, 0, 0.65);
  background: rgba(255, 255, 255, 0.65);
}

.upload-loading-spinner {
  width: 18px;
  height: 18px;
  border: 2px solid #d9d9d9;
  border-top-color: #1677ff;
  border-radius: 50%;
  animation: upload-loading-spin 0.8s linear infinite;
}

@keyframes upload-loading-spin {
  to {
    transform: rotate(360deg);
  }
}

.editor-container :deep(.w-e-text-container) {
  color: #000;
  background-color: #fff;
  min-height: 200px;
}

.editor-container :deep([data-slate-editor]) {
  min-height: 200px;
  height: 100%;
  caret-color: #000;
  padding-top: 15px !important;
}

.editor-container :deep([data-slate-editor] p:first-child) {
  margin-top: 0 !important;
}

.editor-container :deep(.w-e-text-placeholder) {
  pointer-events: none !important;
  background-color: transparent !important;
  top: 15px !important;
}

.editor-container :deep(.w-e-text-container:focus-within .w-e-text-placeholder) {
  display: none;
}

.fullscreen-btn {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 0;
  cursor: pointer;
  color: rgba(0, 0, 0, 0.65);
  font-size: 14px;
}

.fullscreen-btn:hover {
  background-color: #f0f0f0;
  color: #000;
}

.editor-container.is-fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 99999;
  border-radius: 0;
  background-color: #fff;
  display: flex;
  flex-direction: column;
}

.editor-container.is-fullscreen .editor-wrapper {
  flex: 1;
  overflow: hidden;
}
</style>
